import { useState, useEffect } from "react";
import { initials } from "../lib/api";

/**
 * Renders an <img> with a graceful fallback to a circular initials bubble
 * (accent color) when src is empty or fails to load. Never shows alt text
 * as a visible fallback.
 *
 * Props:
 *  - src: image URL (relative or absolute). Empty/null → fallback shown.
 *  - name: full name used to compute initials. Defaults to "Berenice".
 *  - size: pixel diameter (number). Default 96.
 *  - className, style: passed to the wrapper element.
 *  - testId: data-testid attribute.
 */
export default function ProfileAvatar({
  src,
  name = "Berenice",
  size = 96,
  className = "",
  style = {},
  testId,
}) {
  const [errored, setErrored] = useState(false);

  // Reset error if src changes
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const showFallback = !src || errored;
  const baseStyle = {
    width: size,
    height: size,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    ...style,
  };

  if (showFallback) {
    return (
      <div
        className={`profile-avatar-fallback ${className}`}
        data-testid={testId}
        style={{
          ...baseStyle,
          background: "var(--accent-bg)",
          color: "var(--accent)",
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: Math.max(14, Math.round(size * 0.42)),
          letterSpacing: "-0.01em",
        }}
      >
        {initials(name) || "B"}
      </div>
    );
  }

  return (
    <img
      className={className}
      data-testid={testId}
      src={src}
      alt=""
      onError={() => setErrored(true)}
      style={baseStyle}
    />
  );
}
